import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import ArcGauge from "./ArcGauge";
import DemoBadge from "./DemoBadge";
import PulseDot from "./PulseDot";
import AnimatedMetric from "./AnimatedMetric";
import { Gauge, Fuel, TrafficCone } from "lucide-react";

export const TrafficHotspots = ({ city = "Port St. Lucie, FL" }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/engine/traffic-hotspots", { params: { city } })
      .then(r => alive && setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [city]);

  if (!d) return <div className="glass p-6 animate-pulse"><div className="h-40"/></div>;

  return (
    <div className="glass p-0 overflow-hidden" data-testid="traffic-hotspots">
      <div className="p-5 border-b border-gold-dim/20 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-electric">
            <PulseDot /> Traffic Congestion · AuditIQ Intelligence
          </div>
          <h3 className="font-display text-xl mt-1">{d.city}</h3>
        </div>
        <DemoBadge />
      </div>

      <div className="grid lg:grid-cols-[260px_1fr_1fr] gap-4 p-5">
        <div className="panel p-4 flex flex-col items-center justify-center">
          <ArcGauge value={d.rank_score} max={100} label={`Traffic Rank · ${d.overall_rank}`}
                    colorStart="#ECC94B" colorEnd="#C9A84C" size={200} testid="traffic-gauge" />
          <div className="text-xs text-cream-dim mt-2">Ranked #{d.ranked_position} of {d.peer_cities_count}</div>
        </div>

        <div className="panel p-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-steel mb-3 flex items-center gap-2">
            <Gauge size={12} /> Key congestion statistics
          </div>
          <ul className="space-y-3 text-sm">
            <Stat label="Congestion vs state avg" tone="critical" value={`+${d.congestion_vs_state_pct}%`} />
            <Stat label="Accidents vs state avg" tone="critical" value={`+${d.accidents_vs_state_pct}%`} />
            <Stat label="Traffic delay" tone="warning" value={`${d.traffic_delay_pct}% longer`} />
            <Stat label="Fuel wasted / week" tone="warning"
                  value={<><AnimatedMetric value={d.fuel_wasted_gallons_per_week} decimals={0} /> gal</>} icon={<Fuel size={12} />} />
          </ul>
        </div>

        <div className="panel p-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-steel mb-3 flex items-center gap-2">
            <TrafficCone size={12} /> AuditIQ Recommendations
          </div>
          <ul className="space-y-3">
            {d.recommendations.map(r => (
              <li key={r.title} className="border-l-2 border-electric pl-3 py-1">
                <div className="text-cream text-sm">{r.title}</div>
                <div className="text-xs text-cream-dim">{r.detail}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--auditiq-success)] mt-1">
                  Projected impact: -{r.impact_pct}%
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="px-5 pb-5">
        <div className="text-[11px] uppercase tracking-[0.22em] text-steel mb-2">Hotspots</div>
        <div className="grid md:grid-cols-4 gap-3">
          {d.hotspots.map(h => (
            <div key={h.name} className="panel p-3" data-testid={`hotspot-${h.name.replace(/\W+/g,'-').toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="font-display text-cream">{h.name}</div>
                <span className={`text-xs px-2 py-0.5 rounded font-mono ${
                  h.rank === "D" ? "bg-[rgba(229,62,62,0.18)] text-[color:var(--auditiq-critical)]" :
                  h.rank === "C-" ? "bg-[rgba(236,201,75,0.18)] text-[color:var(--auditiq-warning)]" :
                  "bg-[rgba(45,156,219,0.18)] text-electric"
                }`}>{h.rank}</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel mt-2">Current · Target flow</div>
              <div className="text-sm text-cream-dim mt-1">
                <span className="metric text-xl text-cream">{h.current_flow}</span>
                <span className="text-steel"> / {h.target_flow}</span> mph
              </div>
              <div className="text-[10px] text-gold mt-2">↗ {h.alert}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, tone, icon }) => {
  const color = tone === "critical" ? "text-[color:var(--auditiq-critical)]" :
                tone === "warning" ? "text-[color:var(--auditiq-warning)]" : "text-electric";
  return (
    <li className="flex items-center justify-between text-cream-dim">
      <span className="flex items-center gap-2 text-xs">{icon}{label}</span>
      <span className={`font-mono font-semibold ${color}`}>{value}</span>
    </li>
  );
};

export default TrafficHotspots;
